/**
 * El NumberInput respeta los parámetros de ambiente:
 * - AMOUNT_DECIMAL_SEPARATOR
 * - AMOUNT_DECIMAL_ZEROS
 * - CHAR_DECIMAL_SEPARATOR
 * - CHAR_THOUS_SEPARATOR
 * - ADD_THOUSAND_SEPARATOR
 */

import React, { useRef } from 'react';
import { Input, InputProps } from '@apia/theme';

export type TNumberInputChangeEvent = { error: boolean; value: string };

export type TNumberInput = Omit<
  InputProps,
  'type' | 'onChange' | 'onBlur' | 'value' | 'defaultValue'
> & {
  onChange?: (ev: TNumberInputChangeEvent) => unknown;
  value?: string;
  defaultValue?: string;
  avoidDecimalRestriction?: boolean;
  deleteValueOnError?: boolean;
};

function fillZeros(amount: number) {
  return Array(Math.max(0, amount)).fill(0).join('');
}

function fillThousandsZeros(value: number) {
  const stringValue = String(value);
  return `${fillZeros(3 - stringValue.length)}${String(value)}`;
}

const decimalCount = Number(window.AMOUNT_DECIMAL_SEPARATOR);
const decimalZeros = Number(window.AMOUNT_DECIMAL_ZEROS);
const decimalSeparator = window.CHAR_DECIMAL_SEPARATOR;
const thousandsSeparator = window.CHAR_THOUS_SEPARATOR;
const regexpThousandsSeparator =
  thousandsSeparator === '.' ? '\\.' : thousandsSeparator;
const regexpDecimalSeparator =
  decimalSeparator === '.' ? '\\.' : decimalSeparator;

export function parseNumberInputValueToNumber(value: string): number {
  return Number.parseFloat(
    value.replaceAll(thousandsSeparator, '').replaceAll(decimalSeparator, '.'),
  );
}

export function parseNumberValueToNumberInput(value: string | number): string {
  const stringNumber = String(value);
  const result = stringNumber.match(/(-?)(\d+)(?:\.(\d+))/);
  if (!result) return '';

  const isNegative = !!result[1];
  const integer = result[2];
  const decimals = result[3] as string | null;
  const decimalsString = decimals ? `${regexpDecimalSeparator}${decimals}` : '';

  return (
    formatValueToShow(`${isNegative ? '-' : ''}${integer}${decimalsString}`) ||
    ''
  );
}

function formatValueToShow(
  value: string,
  avoidDecimalRestriction = false,
): string | null {
  const result = value.match(
    new RegExp(
      `^(-)?((?:(?:\\d{1,3})${regexpThousandsSeparator}(?:\\d{3}${regexpThousandsSeparator})*(?:\\d{3}))?|(?:\\d*))(?:(?:${regexpDecimalSeparator})(\\d+))?$`,
    ),
  );
  if (!result) return null;

  const isNegative = !!result[1];
  let module = Number(result[2].replaceAll(thousandsSeparator, ''));
  const slicedDecimals = avoidDecimalRestriction
    ? result[3]
    : (result[3]?.slice(0, decimalCount) ?? ''); // 🔹 solo recorto, no paso +1

  let decimalsString: string;
  if (!avoidDecimalRestriction) {
    const noZeroDecimals =
      (slicedDecimals
        ? `${slicedDecimals}` // 🔹 sin redondear, solo uso lo que viene
        : ''
      ).match(/(\d*[1-9])0*/)?.[1] ?? '';
    decimalsString = `${noZeroDecimals}${fillZeros(
      decimalZeros - noZeroDecimals.length,
    )}`;
  } else {
    decimalsString = slicedDecimals ?? '';
  }

  const finalNumberString: string[] = [];
  while (module >= 1000) {
    module /= 1000;
    const truncedInteger = Math.trunc(module);
    finalNumberString.unshift(
      fillThousandsZeros(Math.round((module - truncedInteger) * 1000)),
    );
    module = truncedInteger;
  }
  finalNumberString.unshift(String(module));

  return `${isNegative ? '-' : ''}${finalNumberString.join(
    window.ADD_THOUSAND_SEPARATOR ? window.CHAR_THOUS_SEPARATOR : '',
  )}${decimalsString.length > 0 ? `${decimalSeparator}${decimalsString}` : ''}`;
}

export const NumberInput = React.forwardRef<HTMLInputElement, TNumberInput>(
  (
    { value, avoidDecimalRestriction, deleteValueOnError = true, ...props },
    ref,
  ) => {
    const [actualValue, setActualValue] = React.useState<string>('');
    const [showValue, setShowValue] = React.useState<string>('');
    const lastEmittedValue = React.useRef<string>('');
    const inError = useRef(false);

    const setValue = React.useCallback(
      (newValue: string) => {
        if (newValue === '') {
          if (newValue === lastEmittedValue.current) return;
          setActualValue('');
          setShowValue('');
          props.onChange?.({ value: '', error: false });
          inError.current = false;
        } else {
          const parsedValue = formatValueToShow(
            newValue,
            avoidDecimalRestriction,
          );
          if (parsedValue === null) {
            if (deleteValueOnError) {
              setActualValue('');
              setShowValue('');
            }
            props.onChange?.({ value: '', error: true });
            inError.current = true;
          } else {
            if (newValue === lastEmittedValue.current) {
              setShowValue(parsedValue);
              return;
            }
            setActualValue(newValue);
            setShowValue(parsedValue);
            props.onChange?.({ value: parsedValue, error: false });
            inError.current = false;
          }
        }
        lastEmittedValue.current = newValue;
      },
      [avoidDecimalRestriction, deleteValueOnError, props],
    );

    React.useEffect(() => {
      if (value !== actualValue) setValue((value as string) ?? '');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <Input
        {...props}
        onFocus={React.useCallback(
          (ev: React.FocusEvent<HTMLInputElement>) => {
            props.onFocus?.(ev);
            if (!inError.current || deleteValueOnError !== false) {
              setShowValue(
                String(actualValue).replaceAll(thousandsSeparator, ''),
              );
            }
          },
          [actualValue, deleteValueOnError, props],
        )}
        onBlur={React.useCallback(
          (ev: React.FocusEvent<HTMLInputElement>) => {
            setValue(ev.target.value);
          },
          [setValue],
        )}
        onChange={React.useCallback(
          (ev: React.ChangeEvent<HTMLInputElement>) => {
            setShowValue(ev.target.value);
          },
          [],
        )}
        type="text"
        ref={ref}
        value={showValue}
      />
    );
  },
);
